<template>
  <div v-if="isLoading || isExistingPaymentMethodsLoading || isAvailablePaymentMethodsLoading">
    <div class="tw:animate-pulse tw:bg-gray-300 tw:rounded tw:w-full tw:h-80 tw:mt-6" aria-hidden="true"></div>
  </div>
  <div v-else-if="isError" class="tw:text-red-700 tw:mt-6" v-html="$t('errors.wrong_data_from_app')" />
  <div v-else class="tw:mt-6 tw:flex tw:flex-col">
    <router-link
      class="tw:font-bold tw:text-blue-700 tw:mb-6"
      :to="{ name: 'billing-payment-methods', query: { editPayment: 'true' } }"
    >
      ← {{ $t('setup_back') }}
    </router-link>
    <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-y-4 tw:md:gap-x-6">
      <div class="tw:md:w-1/2">
        <BaseCard class="tw:h-fit" noPadding>
          <div class="tw:flex tw:flex-col tw:gap-3 tw:p-6">
            <h3 class="tw:font-bold tw:mb-1 tw:text-xl" ref="billingFormTitle">
              {{ $t('paymentMethods.billingAddress.heading') }}
            </h3>
            <BillingInformationForm
              ref="billingForm"
              :initialBillingFormValues="existingBillingInformation"
              @onValidateAllSuccess="onValidateBillingSuccess"
              @onValidateAllError="onValidateBillingError"
              @setBillingFormValid="setBillingFormValid"
            />
          </div>
        </BaseCard>
      </div>
      <div class="tw:md:w-1/2 tw:flex tw:flex-col">
        <div class="sticky">
          <BaseCard class="tw:h-fit" noPadding>
            <div class="tw:flex tw:flex-col tw:gap-3">
              <h3 class="tw:font-bold tw:text-xl tw:mt-6 tw:mx-4 tw:mb-1">
                {{ $t('paymentMethods.heading') }}
              </h3>
              <div
                v-if="isExistingPaymentMethodsLoading || isAvailablePaymentMethodsLoading"
                class="tw:animate-pulse tw:bg-gray-300 tw:rounded tw:h-80 tw:mx-6 tw:mb-6"
                aria-hidden="true"
              />
              <PaymentMethodForm
                v-else
                source="billing-information-form"
                :currency="currency"
                :accommodationId="selectedAccommodationId"
                :productName="zuoraMetricConstants.productName.settings"
                :fetchingPaymentMethods="isFetching || isExistingPaymentMethodsLoading"
                :existingPaymentMethods="existingPaymentMethods"
                :availablePaymentMethods="availablePaymentMethodsData"
                :paypalConfig="paypalConfig"
                :selectedPaymentMethod="selectedPaymentMethod"
                @onSelectedValueChange="e => (selectedPaymentMethod = e)"
                @onShowNewFormCancel="onNewPaymentMethodFormCancel"
                @onZuoraCallbackSuccess="onZuoraCallbackSuccess"
                @onZuoraCallbackError="onZuoraCallbackError"
                @onZuoraClientValidationError="onZuoraClientValidationError"
              />
            </div>
          </BaseCard>
          <div class="tw:flex tw:justify-end tw:gap-6 tw:mt-6">
            <GhostButton @click="goBackAction">{{ $t('payment_method_cancel_cta') }}</GhostButton>
            <MainButton @click="updateBillingInformation" :disabled="isButtonDisabled">
              <span v-if="!isSubmittingForm">{{ $t('payment_method_update_cta') }}</span>
              <span v-if="isSubmittingForm">
                <SmallDotLoader color="#fff" />
              </span>
            </MainButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, nextTick, watch, onBeforeUnmount, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'

import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import BaseCard from '@/components/dashboard/BaseCard.vue'
import BillingInformationForm from '@/components/payment/BillingInformationForm.vue'
import PaymentMethodForm from '@/components/payment/PaymentMethodForm.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'

import {
  useAvailablePaymentMethods,
  useErrorMetrics,
  useExistingPaymentMethods,
  useZuoraAccount,
  useZuoraUpdateAccountMutation,
} from '@/components/payment/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { countries } from '@/constants/countries'

import useToastNotifications from '@/components/notifications/useToastNotifications'
import zuoraMetricConstants from '@/constants/zuoraMetricConstants'
import usePaymentNotifications from '@/components/notifications/usePaymentNotifications'
import { useRateConnect } from '@/components/rateConnect/useRateConnect'
import { useStudioPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useStudioPlusReactivation'
import { useRcPlusCrossSell } from '@/composables/useRcPlusCrossSell'
import { useSessionValues } from '@/composables/useSessionValues'
import { useSelectedPaymentMethodType } from '@/composables/useSelectedPaymentMethodType'
import type { PaymentMethodType } from '@/services/premiumService/PremiumService'

/** Billing information form data */
interface BillingInformation {
  companyName: string
  email: string
  firstName: string
  lastName: string
  address: string
  city: string
  zipCode: string
  country: string
  state: string
  taxId?: string
  noTaxId?: boolean
}

/** Update Zuora account body structure */
interface UpdateZuoraAccountBody {
  name?: string
  defaultPaymentMethodId?: string
  billToContact: {
    address1?: string
    city?: string
    firstName?: string
    lastName?: string
    workEmail?: string
    zipCode: string
    country: string
    state: string
  }
  taxInfo: {
    exemptCertificateId: string
  }
  paymentMethodType?: PaymentMethodType
}

/** Billing form component ref type */
interface BillingFormRef {
  validateAll: () => void
  formUpdated: boolean
}

const router = useRouter()

const { clearAllNotifications } = usePaymentNotifications()
const { toggleResumeCampaign, togglePauseCampaign } = useRateConnect()
const { openReactivationModal, accommodationId } = useStudioPlusReactivation()
const { openSubscriptionModal } = useRcPlusCrossSell()
const { displayNotification } = useToastNotifications()
const { sendErrorMetrics } = useErrorMetrics()
const { language, currency, userRole } = useSessionValues()

const billingForm = ref<BillingFormRef | null>(null)
const billingFormTitle = ref<HTMLHeadingElement | null>(null)
const selectedPaymentMethod = ref<string | null>(null)
const isBillingFormValid = ref<boolean>(true)
const newBillingInformation = ref<BillingInformation | null>(null)
const isZuoraHppLoadError = ref<boolean>(false)
const isSubmittingForm = ref<boolean>(false)

/** Countries that require state/province field */
const countriesWithState: string[] = ['US', 'MX', 'CA', 'BR']

const { isLoading, isError, zuoraAccountData } = useZuoraAccount()
const { existingPaymentMethods, isLoading: isExistingPaymentMethodsLoading } =
  useExistingPaymentMethods(selectedAccommodationId)
const { updateZuoraAccount } = useZuoraUpdateAccountMutation()

const {
  isFetching,
  isLoading: isAvailablePaymentMethodsLoading,
  availablePaymentMethodsData,
  paypalConfig,
  refetch,
} = useAvailablePaymentMethods(selectedAccommodationId, currency)

/**
 * Existing billing information from Zuora account
 */
const existingBillingInformation: ComputedRef<BillingInformation | null> = computed(() => {
  if (!zuoraAccountData.value) return null

  const countryCode = countries.find(country => country.name === zuoraAccountData.value?.billToContact?.country)?.code

  return {
    companyName: zuoraAccountData.value.basicInfo.name,
    email: zuoraAccountData.value.billToContact.workEmail,
    firstName: zuoraAccountData.value.billToContact.firstName,
    lastName: zuoraAccountData.value.billToContact.lastName,
    address: zuoraAccountData.value.billToContact.address1,
    city: zuoraAccountData.value.billToContact.city,
    zipCode: zuoraAccountData.value.billToContact.zipCode,
    country: countryCode || '',
    state: zuoraAccountData.value.billToContact.state || '',
    taxId: zuoraAccountData.value.taxInfo?.exemptCertificateId,
    noTaxId: !zuoraAccountData.value.taxInfo?.exemptCertificateId,
  }
})

/**
 * Default payment method ID
 */
const defaultPaymentMethodId: ComputedRef<string | undefined> = computed(
  () => existingPaymentMethods.value?.find(paymentMethod => paymentMethod.isDefault)?.id
)

/**
 * Check if selected payment method is a new one (CREDIT_CARD or SEPA)
 */
const isSelectedPaymentMethodNew: ComputedRef<boolean> = computed(
  () => selectedPaymentMethod.value === 'CREDIT_CARD' || selectedPaymentMethod.value === 'SEPA'
)

const selectedPaymentMethodType = useSelectedPaymentMethodType(selectedPaymentMethod, existingPaymentMethods)

/**
 * Button disabled state logic
 */
const isButtonDisabled: ComputedRef<boolean> = computed(() => {
  if (billingForm.value?.formUpdated && isBillingFormValid.value) return false
  else if (isSubmittingForm.value) return true
  else if (defaultPaymentMethodId.value !== selectedPaymentMethod.value) return false
  else return true
})

// ==================== Functions ====================

/**
 * Set billing form validation state
 */
const setBillingFormValid = (isValid: boolean): void => {
  isBillingFormValid.value = isValid
}

/**
 * Get tax ID value (handles noTaxId checkbox)
 */
const getTaxId = (): string => {
  if (newBillingInformation.value) {
    if (newBillingInformation.value.noTaxId) return ''
    else if (newBillingInformation.value.taxId) return newBillingInformation.value.taxId
  }
  return existingBillingInformation.value?.taxId || ''
}

/**
 * Force re-render of payment methods after error
 */
const forceRender = async (): Promise<void> => {
  await nextTick()
  refetch()
  isZuoraHppLoadError.value = false
  isSubmittingForm.value = false
}

/**
 * Handle successful Zuora HPP callback
 */
const onZuoraCallbackSuccess = async (refId: string): Promise<void> => {
  sendZuoraAccount(refId)
}

/**
 * Handle Zuora client-side validation errors
 */
const onZuoraClientValidationError = (errorMessage: string, isAttemptExceedLimitationError: boolean): void => {
  isSubmittingForm.value = false
  if (isAttemptExceedLimitationError) forceRender()
}

/**
 * Handle Zuora HPP callback errors
 */
const onZuoraCallbackError = (withRerender: boolean = false): void => {
  if (isSubmittingForm.value) {
    displayNotification({
      message: 'subscriptions.details.transactionHistory.error',
      isTranslationKey: true,
      type: 'error',
    })
  } else {
    if (withRerender) {
      forceRender()
    }
    isZuoraHppLoadError.value = true
  }
  isSubmittingForm.value = false
}

/**
 * Handle canceling new payment method form
 */
const onNewPaymentMethodFormCancel = (): void => {
  if (isSelectedPaymentMethodNew.value) {
    selectedPaymentMethod.value = defaultPaymentMethodId.value || null
  }
}

/**
 * Trigger billing form validation
 */
const updateBillingInformation = (): void => {
  if (isSelectedPaymentMethodNew.value && isZuoraHppLoadError.value) {
    displayNotification({
      message: 'subscriptions.details.transactionHistory.error',
      isTranslationKey: true,
      type: 'error',
    })
    return
  }
  isSubmittingForm.value = true
  billingForm.value?.validateAll()
}

/**
 * Handle successful billing form validation
 */
const onValidateBillingSuccess = (billingInfo: BillingInformation): void => {
  newBillingInformation.value = billingInfo

  if (!selectedPaymentMethod.value) {
    isSubmittingForm.value = false
    return
  }

  if (isSelectedPaymentMethodNew.value) {
    if (userRole.value === 'support') {
      isSubmittingForm.value = false
      return
    }

    Z.submit()
    return
  }

  if (selectedPaymentMethod.value !== defaultPaymentMethodId.value) {
    sendZuoraAccount(selectedPaymentMethod.value)
    return
  }

  sendZuoraAccount()
}

/**
 * Handle billing form validation errors
 */
const onValidateBillingError = (): void => {
  isSubmittingForm.value = false
  window.scrollTo({
    behavior: 'smooth',
    top: (billingFormTitle.value?.getBoundingClientRect().top || 0) + window.scrollY - 125,
  })
}

/**
 * Send updated billing information to Zuora
 */
const sendZuoraAccount = async (refId?: string): Promise<void> => {
  const body: UpdateZuoraAccountBody = {
    billToContact: {
      zipCode: '',
      country: '',
      state: '',
    },
    taxInfo: {
      exemptCertificateId: '',
    },
  }

  if (refId) body.defaultPaymentMethodId = refId

  // Only include changed fields
  if (
    newBillingInformation.value?.companyName &&
    newBillingInformation.value.companyName !== existingBillingInformation.value?.companyName
  ) {
    body.name = newBillingInformation.value.companyName
  }

  if (
    newBillingInformation.value?.address &&
    newBillingInformation.value.address !== existingBillingInformation.value?.address
  ) {
    body.billToContact.address1 = newBillingInformation.value.address
  }

  if (
    newBillingInformation.value?.city &&
    newBillingInformation.value.city !== existingBillingInformation.value?.city
  ) {
    body.billToContact.city = newBillingInformation.value.city
  }

  if (
    newBillingInformation.value?.firstName &&
    newBillingInformation.value.firstName !== existingBillingInformation.value?.firstName
  ) {
    body.billToContact.firstName = newBillingInformation.value.firstName
  }

  if (
    newBillingInformation.value?.lastName &&
    newBillingInformation.value.lastName !== existingBillingInformation.value?.lastName
  ) {
    body.billToContact.lastName = newBillingInformation.value.lastName
  }

  if (
    newBillingInformation.value?.email &&
    newBillingInformation.value.email !== existingBillingInformation.value?.email
  ) {
    body.billToContact.workEmail = newBillingInformation.value.email
  }

  body.billToContact.zipCode = newBillingInformation.value?.zipCode ?? existingBillingInformation.value?.zipCode ?? ''
  body.billToContact.country = newBillingInformation.value?.country ?? existingBillingInformation.value?.country ?? ''
  body.billToContact.state = countriesWithState.includes(body.billToContact.country)
    ? newBillingInformation.value?.state || ''
    : ''

  body.taxInfo.exemptCertificateId = getTaxId()
  body.paymentMethodType = selectedPaymentMethodType.value

  if (Object.keys(body).length > 0) {
    await updateZuoraAccount(
      { body, language: language.value },
      {
        onSuccess: () => {
          const query = router.currentRoute.value.query

          if (query?.['isResumeFlow'] === 'true') {
            returnFromResumeFlow('true')
          } else if (query?.['isPauseUntil'] === 'true') {
            togglePauseCampaign('open')
            router.push({
              name: query?.['sourceRoute'] as string,
              query: { isUpdated: 'true' },
            })
          } else {
            router.push({ name: 'billing-payment-methods', query: { editPayment: 'true' } })
          }
        },
        onError: (response: any) => {
          sendErrorMetrics({
            itemId: selectedAccommodationId.value,
            productName: zuoraMetricConstants.productName.settings,
            code: zuoraMetricConstants.metricErrorCodes.billingInformation,
            message: response.response?.data?.message,
          })
          forceRender()
          displayNotification({
            message: 'subscriptions.details.transactionHistory.error',
            isTranslationKey: true,
            type: 'error',
          })
        },
      }
    )
  }
}

/**
 * Return to the flow that opened edit payment (resume RC, Plus reactivation, or RC Plus cross-sell).
 */
const returnFromResumeFlow = (isUpdated: 'true' | 'false'): void => {
  const query = router.currentRoute.value.query

  if (query?.['productType'] === 'RATE_CONNECT') {
    toggleResumeCampaign('open')
  } else if (query?.['productType'] === 'RC_PLUS_CROSS_SELL') {
    openSubscriptionModal()
  } else {
    openReactivationModal(accommodationId, 'payment-details-update-page')
  }

  router.push({
    name: query?.['sourceRoute'] as string,
    query: { isUpdated },
  })
}

/**
 * Handle back button / cancel action
 */
const goBackAction = (): void => {
  const query = router.currentRoute.value.query

  if (query?.['isResumeFlow'] === 'true') {
    returnFromResumeFlow('false')
  } else if (query?.['isPauseUntil'] === 'true') {
    togglePauseCampaign('open')
    router.push({
      name: query?.['sourceRoute'] as string,
      query: { isUpdated: 'false' },
    })
  } else {
    router.push({ name: 'billing-payment-methods', query: { editPayment: 'true' } })
  }
}

// ==================== Watchers ====================

/**
 * Sync selected payment method with default when it changes
 */
watch(defaultPaymentMethodId, newVal => {
  selectedPaymentMethod.value = newVal || null
})

/**
 * Redirect if no Zuora account data available
 */
watch(
  [isLoading, zuoraAccountData],
  ([loading, account]) => {
    if (!loading && !account) {
      router.push({ name: 'billing-payment-methods' })
    }
  },
  { immediate: true }
)

// ==================== Lifecycle ====================

onMounted(() => {
  if (defaultPaymentMethodId.value) {
    selectedPaymentMethod.value = defaultPaymentMethodId.value
  }

  // Scroll to form title
  window.scrollTo({
    behavior: 'smooth',
    top: (billingFormTitle.value?.getBoundingClientRect().top || 0) + window.scrollY - 125,
  })
})

onBeforeUnmount(() => {
  clearAllNotifications()
})
</script>

<style scoped>
.sticky {
  position: sticky;
  top: 120px;
  right: 0;
}
</style>
